import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Mic, MicOff, Loader2 } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";

export default function VoicePrescription({ patientId, initial = "", onPrescriptionSaved = null }) {
  const [text, setText] = useState(initial);
  const [listening, setListening] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState("en-IN");
  const recRef = useRef(null);

  const start = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice input not supported by this browser"); return; }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = lang;
    let final = text;
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += t + " ";
        else interim += t;
      }
      setText((final + interim).trimStart());
    };
    rec.onerror = (e) => { setListening(false); toast.error(`Voice error: ${e.error}`); };
    rec.onend = () => setListening(false);
    rec.start();
    recRef.current = rec;
    setListening(true);
  };

  const stop = () => { recRef.current?.stop(); setListening(false); };

  // ✅ Complete automatic workflow
  const save = async () => {
    if (!text.trim()) {
      toast.error("Please record or enter a prescription");
      return;
    }

    setSaving(true);
    try {
      const response = await api.post("/prescription/voice-to-structured-and-save", {
        patient_id: patientId,
        voice_text: text
      });

      if (response.data.success) {
        toast.success("✅ Prescription sent to patient via WhatsApp!");
        
        if (onPrescriptionSaved) {
          onPrescriptionSaved(response.data);
        }

        setText("");
      } else {
        toast.error(`Error: ${response.data.error}`);
      }
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.response?.data?.error || "Prescription save failed");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => () => recRef.current?.stop(), []);

  return (
    <div className="mt-4 rounded-lg border bg-white p-3 space-y-2" data-testid="voice-prescription">
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>Voice Prescription · {lang === "hi-IN" ? "हिन्दी" : "English"}</span>
        <div className="flex gap-2">
          <button onClick={() => setLang(lang === "en-IN" ? "hi-IN" : "en-IN")} className="text-sky-600 hover:underline" data-testid="voice-lang-toggle">{lang === "en-IN" ? "हिं" : "EN"}</button>
        </div>
      </div>

      <Textarea 
        value={text} 
        onChange={(e) => setText(e.target.value)} 
        rows={3} 
        placeholder="Tap mic and dictate the prescription…" 
        data-testid="prescription-textarea" 
      />

      <div className="flex gap-2 flex-wrap">
        {listening ? (
          <Button 
            onClick={stop} 
            size="sm" 
            variant="destructive" 
            className="gap-1" 
            data-testid="mic-stop"
          >
            <MicOff className="w-4 h-4" /> Stop
          </Button>
        ) : (
          <Button 
            onClick={start} 
            size="sm" 
            className="bg-sky-500 hover:bg-sky-600 text-white gap-1" 
            data-testid="mic-start"
          >
            <Mic className="w-4 h-4" /> Record
          </Button>
        )}

        <Button 
          onClick={save} 
          size="sm" 
          variant="outline"
          disabled={saving || !text.trim()} 
          data-testid="prescription-save"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Saving…
            </>
          ) : (
            "Save & Send"
          )}
        </Button>
      </div>
    </div>
  );
}
